import { Form, Input, Select } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentsList } from "@/modules/departments/hooks/useDepartments";
import { DepartmentTreeSelect } from "@/shared/components/DepartmentTreeSelect";
import { FormModal } from "@/shared/components/FormModal";
import { useRoleOptions } from "../hooks/useUsers";
import type { CreateUserInput, ManagedUser } from "../types";

/** `FormModal` ning ichki formasi — `scope`ni `department:‹uuid›` shakliga
 * yig'ishdan OLDINGI qiymatlar (foydalanuvchi bo'lim ID sini tanlaydi,
 * prefiksni o'zi yozmaydi). */
type RawFormValues = Omit<CreateUserInput, "scope"> & { departmentId?: string };

interface UserFormModalProps {
	open: boolean;
	/** `null` — yaratish rejimi, aks holda tahrirlash. */
	editing: ManagedUser | null;
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (values: CreateUserInput) => void;
}

export function UserFormModal({
	open,
	editing,
	isSubmitting,
	onCancel,
	onSubmit,
}: UserFormModalProps) {
	const { t } = useTranslation("users");
	const isEditing = editing !== null;

	// `FormModal` `initialValues` o'zgarganda formani qayta to'ldiradi. Har renderda
	// yangi obyekt yasalsa, ota-komponent qayta renderlanishi (masalan `isFetching`
	// almashuvi) kiritilgan parolni o'chirib yuboradi.
	const initialValues = useMemo(() => (editing ? { phone: editing.phone } : undefined), [editing]);

	// ⚠️ `roleId` FAQAT yaratishda so'raladi — `UserUpdateRequestSchema`da
	// bu maydon umuman yo'q, mavjud foydalanuvchining rolini `AssignRoleModal`
	// almashtiradi (spec 2026-09-12-p5 §3.5/§4.2).
	const { data: roles = [] } = useRoleOptions(open && !isEditing);
	const { data: departments = [] } = useDepartmentsList();

	return (
		<FormModal<RawFormValues>
			open={open}
			title={isEditing ? t("form.titleEdit") : t("form.titleNew")}
			initialValues={initialValues}
			isSubmitting={isSubmitting}
			onCancel={onCancel}
			onSubmit={({ departmentId, ...values }) =>
				onSubmit({
					...values,
					scope: departmentId ? `department:${departmentId}` : undefined,
				})
			}
		>
			<Form.Item
				name="phone"
				label={t("form.phoneLabel")}
				rules={[
					{ required: true, message: t("form.phoneRequired") },
					{ pattern: /^\+?[1-9]\d{1,14}$/, message: t("form.phoneFormat") },
				]}
			>
				<Input placeholder={t("form.phonePlaceholder")} autoComplete="tel" />
			</Form.Item>

			<Form.Item
				name="password"
				label={isEditing ? t("form.passwordLabelEdit") : t("form.passwordLabelNew")}
				rules={
					isEditing
						? [{ min: 4, message: t("form.passwordMin") }]
						: [
								{ required: true, message: t("form.passwordRequired") },
								{ min: 4, message: t("form.passwordMin") },
							]
				}
			>
				<Input.Password placeholder={t("form.passwordPlaceholder")} autoComplete="new-password" />
			</Form.Item>

			{isEditing ? null : (
				<>
					<Form.Item
						name="roleId"
						label={t("common.roleLabel")}
						rules={[{ required: true, message: t("common.roleRequired") }]}
					>
						<Select
							placeholder={t("common.roleRequired")}
							options={roles.map((role) => ({ value: role.id, label: role.name }))}
						/>
					</Form.Item>

					<Form.Item name="departmentId" label={t("common.departmentScopeLabel")}>
						<DepartmentTreeSelect
							departments={departments}
							placeholder={t("common.departmentScopePlaceholder")}
						/>
					</Form.Item>
				</>
			)}
		</FormModal>
	);
}
